import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import { Card, Box, Typography, Button, Stack } from '@mui/material'
import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { BasicTable, type BasicTableRow, type BasicTableColumn } from '../BasicTable'
import { AddCoachingPlanModal } from './AddCoachingModal'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Modal } from '../../../../customComponents/Modal'
import { type CoachingPlan } from '../../../../../models/CoachingPlan'
import { DeleteCoachingPlan, GetCoachingPlans, GetOrganizationFromPlanOrgId, LockActivity, UnlockActivity, UpdateCoachingPlan } from '../../../../../services/AnnualPlanService'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { ArrowBack, OpenInNew } from '@mui/icons-material'
import { type AnnualPlanRole } from '../../../../../models/AnnualPlanRole'
import type { OrganizationDetailsModel } from '../../../../../models/OrganizationDetailsModel'
import { PriorityTwoActivityName } from '../../../../../models/PriorityTwoActivityName'
import { type ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import { SubBasicTable, type SubBasicTableColumn, type SubBasicTableRow } from '../SubBasicTable'
import { CoachingPlanTrackingModal } from './CoachingPlanTrackingModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

interface Props {
  priorityId: number
  planOrgId: number
  userRole: AnnualPlanRole | null
  readOnly: boolean
  trackingReadonly: boolean
  onBack: () => void
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  isTracking?: boolean
  handleViewContract?: () => void
  budgetOverviewView?: boolean
}

interface ChecklistRow extends BasicTableRow {
  id: number
  coachingPlanId: number
  orgInfo: string[]
  resources: string
  actions: string
  budgetRequest: string
  fullPlan: CoachingPlan
}
interface SubChecklistRow extends SubBasicTableRow {
  fullPlan: CoachingPlan
}

export function CSDevelopmentChecklistList (props: Props): JSX.Element {
  const [showAddCoaching, setShowAddCoaching] = useState(false)
  const [showContinueDeleteCoaching, setShowContinueDeleteCoaching] = useState(false)
  const [coachingPlans, setCoachingPlans] = useState<CoachingPlan[]>([])
  const [coachingPlanToDelete, setCoachingPlanToDelete] = useState<CoachingPlan | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [editingCoachingPlan, setEditingCoachingPlan] = useState<CoachingPlan>()
  const [coachingPlanRows, setCoachingPlanRows] = useState<ChecklistRow[]>([])
  const [organizationName, setOrganizationName] = useState('')
  const [startOnDetailsPage, setStartOnDetailsPage] = useState(false)
  const [coachingPlanToTrack, setCoachingPlanToTrack] = useState<CoachingPlan | null>(null)
  const [coachingPlanWithAdditionalDetailsToDelete, setCoachingPlanWithAdditionalDetailsToDelete] = useState<CoachingPlan | null>(null)
  const [coachingPlanWithTrackingToDelete, setCoachingPlanWithTrackingToDelete] = useState<CoachingPlan | null>(null)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await Promise.all([
        fetchCoachingPlans(),
        fetchOrganizationName()
      ])
      setIsLoading(false)
    }

    void fetchData()
  }, [])

  const handleDeleteCoachingPlanAdditionalDetails = async (p: CoachingPlan): Promise<void> => {
    const updatedPlan: CoachingPlan = { ...p }
    updatedPlan.isImplementationPhase = props.implementationPhase
    updatedPlan.notes = null
    updatedPlan.progress = null
    updatedPlan.progressNotes = null
    updatedPlan.implementationNotes = null
    updatedPlan.budgetSpent = null

    const { success, errors } = await UpdateCoachingPlan(updatedPlan)

    if (!success) {
      errors?.forEach((error) => toast.error(error))
      return
    }

    toast.success('Coaching Plan Updated')
    await fetchCoachingPlans()
    setCoachingPlanWithAdditionalDetailsToDelete(null)
  }

  const handleDeleteCoachingPlanTracking = async (p: CoachingPlan): Promise<void> => {
    const updatedPlan: CoachingPlan = { ...p }
    updatedPlan.isImplementationPhase = props.implementationPhase
    updatedPlan.progress = null
    updatedPlan.progressNotes = null
    updatedPlan.implementationNotes = null
    updatedPlan.budgetSpent = null

    const { success, errors } = await UpdateCoachingPlan(updatedPlan)

    if (!success) {
      errors?.forEach((error) => toast.error(error))
      return
    }

    toast.success('Coaching Plan Updated')
    await fetchCoachingPlans()
    setCoachingPlanWithTrackingToDelete(null)
  }

  const expandedContent = (p: CoachingPlan): JSX.Element | null => {
    const hasAdditionalDetails = p.notes != null && p.notes?.trim() !== ''
    if (props.implementationPhase !== true || !hasAdditionalDetails) return null
    const hasTrackingDetails = (p?.progress != null && p.progress.trim() !== '') &&
          (p?.progressNotes != null && p.progressNotes?.trim() !== '') &&
          (p?.implementationNotes != null && p.implementationNotes?.trim() !== '')

    const subColumns: SubBasicTableColumn[] = [
      { id: 'notes', label: 'Notes', minWidth: 200 },
      { id: 'action', label: '' },
      { id: 'progress', label: 'Current Progress', isTrackingColumn: true },
      { id: 'progressNotes', label: 'Progress Notes', isTrackingColumn: true },
      { id: 'implementationNotes', label: 'Implementation Notes', isTrackingColumn: true },
      { id: 'budgetSpent', label: 'Budget Spent', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true }
    ]
    const subRows: SubChecklistRow[] = [
      {
        id: p.coachingPlanId ?? 0,
        notes: p?.notes ?? '',
        progress: p?.progress ?? '',
        progressNotes: p?.progressNotes ?? '',
        implementationNotes: p?.implementationNotes ?? '',
        budgetSpent: isValidPositiveNumber(p.budgetSpent ?? null) ? '$' + Number(p.budgetSpent).toLocaleString() : '',
        fullPlan: p
      }
    ]

    const handleEditAdditionalDetails = (r: SubChecklistRow): void => {
      setStartOnDetailsPage(true)
      void handleEditCoaching(r)
    }
    const handleDeleteAdditionalDetails = async (r: SubChecklistRow): Promise<void> => {
      setCoachingPlanWithAdditionalDetailsToDelete(r.fullPlan)
    }
    const handleEditTracking = async (r: SubChecklistRow): Promise<void> => {
      void handleTrackCoaching(r)
    }
    const handleDeleteTracking = async (r: SubChecklistRow): Promise<void> => {
      setCoachingPlanWithTrackingToDelete(r.fullPlan)
    }

    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          dataTestId={'csDevChecklistSubTable'}
          columns={subColumns}
          rows={subRows}
          hasTrackingDetails={hasTrackingDetails}
          onEditAdditionalDetails={handleEditAdditionalDetails}
          onDeleteAdditionalDetails={handleDeleteAdditionalDetails}
          onEditTracking={handleEditTracking}
          onDeleteTracking={handleDeleteTracking}
          hideDelete={props.isTracking}
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadonly}
        />
      </Box>
    )
  }

  const fetchCoachingPlans = async (): Promise<void> => {
    if (props.priorityId === 0) return
    const plans = await handleApiResponse(GetCoachingPlans(props.priorityId, props.implementationPhase !== true))
    if (plans == null) return
    const rows: ChecklistRow[] = []
    plans.forEach(p => {
      const orgInfoString: string[] = []
      orgInfoString.push(p.organizationName)

      p.contacts.forEach(c => {
        orgInfoString.push(c.contactName)
        orgInfoString.push(c.contactEmail)
      })

      const row: ChecklistRow = {
        id: p.coachingPlanId ?? 0,
        coachingPlanId: p.coachingPlanId ?? 0,
        orgInfo: orgInfoString,
        resources: p.resources,
        actions: p.actions,
        budgetRequest: `$${isValidPositiveNumber(p.budgetRequest) ? Number(p.budgetRequest).toLocaleString() : ''}`,
        activityLock: p.activityLock,
        fullPlan: p,
        expandedContent: expandedContent(p),
        isUpdated: p.isUpdate ?? false,
        isTracked: p.progress != null,
        needsReview: p.needsReview
      }
      rows.push(row)
    })
    setCoachingPlanRows(rows)
    setCoachingPlans(plans)
  }

  const fetchOrganizationName = async (): Promise<void> => {
    if (props.planOrgId === 0) return
    const org: OrganizationDetailsModel | null = await handleApiResponse(GetOrganizationFromPlanOrgId(props.planOrgId))
    if (org != null) setOrganizationName(org.name)
  }

  const updateCoachingTrainings = async (refresh: boolean): Promise<void> => {
    setShowAddCoaching(false)
    setStartOnDetailsPage(false)

    if (editingCoachingPlan != null) {
      await UnlockActivity(
        editingCoachingPlan.coachingPlanId ?? 0,
        PriorityTwoActivityName.CoachingStructureAndSystems,
        editingCoachingPlan.isUpdate ?? false
      )
    }

    if (refresh) await fetchCoachingPlans()
  }

  const handleEditCoaching = async (coachingPlanRow: ChecklistRow | SubChecklistRow): Promise<void> => {
    setEditingCoachingPlan(coachingPlanRow.fullPlan)
    setShowAddCoaching(true)
    await LockActivity(coachingPlanRow.id, PriorityTwoActivityName.CoachingStructureAndSystems, coachingPlanRow.fullPlan.isUpdate ?? false)
  }

  const handleAddCoaching = (): void => {
    setEditingCoachingPlan(undefined)
    setShowAddCoaching(true)
  }

  const handleDeleteCoaching = (coachingPlan: ChecklistRow): void => {
    setCoachingPlanToDelete(coachingPlan.fullPlan)
    setShowContinueDeleteCoaching(true)
  }

  const continueDeleteCoaching = async (): Promise<void> => {
    await DeleteCoachingPlan(coachingPlanToDelete?.coachingPlanId ?? 0, coachingPlanToDelete?.isUpdate ?? false, props.implementationPhase === true)
    setShowContinueDeleteCoaching(false)
    toast.success('Coaching Plan Deleted')
    await fetchCoachingPlans()
  }

  const handleTrackCoaching = async (coachingPlanRow: ChecklistRow | SubChecklistRow): Promise<void> => {
    setCoachingPlanToTrack(coachingPlanRow.fullPlan)
    await LockActivity(
      coachingPlanRow.id,
      PriorityTwoActivityName.CoachingStructureAndSystems,
      coachingPlanRow.fullPlan.isUpdate ?? false
    )
  }

  const handleCloseTracking = async (): Promise<void> => {
    if (coachingPlanToTrack != null) {
      await UnlockActivity(
        coachingPlanToTrack.coachingPlanId ?? 0,
        PriorityTwoActivityName.CoachingStructureAndSystems,
        coachingPlanToTrack.isUpdate ?? false
      )
    }
    setCoachingPlanToTrack(null)
  }

  const columns: BasicTableColumn[] = [
    { id: 'orgInfo', label: 'ISD/District INFO', minWidth: 200 },
    { id: 'actions', label: 'Actions Planned', minWidth: 100 },
    { id: 'resources', label: 'Resources', minWidth: 200 },
    { id: 'budgetRequest', label: 'Budget', minWidth: 100 }
  ]

  if (props.userRole === null) {
    return <></>
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'Review Data',
      items: [
        <>
          Look at your data from the Coaching Structure Checklist, Coaching Capacity Tool, or other sources you may have about coaching in your ISD and districts to develop priorities and actions.
        </>
      ]
    },
    {
      title: 'Review START Resources',
      items: [
        <>
          Access resources on the <a style={{ color: 'currentcolor' }} target='_blank' href='https://www.gvsu.edu/autismcenter/coaching-21.htm'>START Coaching webpage</a> for ideas and inspiration.
        </>
      ]
    },
    {
      title: 'Align with Other Initiatives',
      items: [
        <>Consider whether you can align with other coaching initiatives in your ISD and districts.</>
      ]
    }
  ]

  return <Box data-testid='csDevChecklist' sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    position: 'relative'
  }}
  >
    {props.showTableOnly !== true && <>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Button
          data-testid='backPdrsButton'
          onClick={props.onBack}
          sx={{
            padding: '5px 10px',
            backgroundColor: 'var(--grey-50)',
            color: 'var(--grey-500)',
            width: 'fit-content'
          }}
        >
          <ArrowBack sx={{ mr: '10px' }} />
          Priority 2 Activities
        </Button>

        {props.implementationPhase === true && (
          <Button
            data-testid="viewContractButton"
            sx={{
              padding: '5px 10px',
              backgroundColor: 'var(--grey-50)',
              color: 'var(--grey-500)',
              width: 'fit-content',
              textTransform: 'none'
            }}
            onClick={props.handleViewContract}
          >
            View Contract
            <OpenInNew fontSize='small' sx={{ ml: '10px' }} />
          </Button>
        )}
      </Stack>

      <Typography variant='h1'>
        Coaching Structure and Systems
      </Typography>

      <Typography variant='body1'>
        Use the Coaching Structure Checklist and coaching resources to develop priorities for your ISD and district coaching structure and systems.
        List the contact information for the lead(s) on this priority, actions planned, resources, and the budget request.
        Include this information for your ISD and any districts that will have specific priorities related to coaching structure and systems.
        Use the Coaching Structure Checklist and other resources available on the <a style={{ color: 'currentcolor' }} target='_blank' href='https://www.gvsu.edu/autismcenter/coaching-21.htm'>START Coaching webpage</a>.
        Click “Things to Consider” for additional information.
      </Typography>

      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton
            onClick={handleAddCoaching}
            dataTestId='addCoachingButton'
          >
            Add Coaching Plan
            <AddCircleIcon sx={{ ml: '0.5em' }} />
          </BlueButton>
        }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='checklistThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='checklistThingsToConsiderCard'
          />
        </Box>
      </Box>
    </>}

    {showAddCoaching &&
      <AddCoachingPlanModal
        onClose={updateCoachingTrainings}
        priorityId={props.priorityId}
        editCoachingPlan={editingCoachingPlan}
        planOrgId={props.planOrgId}
        orgName={organizationName}
        implementationPhase={props.implementationPhase}
        startOnDetailsPage={startOnDetailsPage}
      />
    }

    {coachingPlanToTrack != null && props.implementationPhase === true && (
      <CoachingPlanTrackingModal
        open={true}
        onClose={handleCloseTracking}
        onSaved={() => { void fetchCoachingPlans() }}
        coachingPlan={coachingPlanToTrack}
        implementationPhase={props.implementationPhase}
      />
    )}

    {coachingPlanWithAdditionalDetailsToDelete != null && (
      <BannerModal
        title='Are you sure?'
        cancelButtonText='No, Cancel'
        confirmButtonText='Yes, Delete'
        dataTestId='confirmDeleteCoachingPlanAdditionalDetails'
        bodyContent={'Are you sure you want to delete this item? This will also remove any tracking data associated with it.'}
        onClose={() => { setCoachingPlanWithAdditionalDetailsToDelete(null) }}
        onConfirm={() => { void handleDeleteCoachingPlanAdditionalDetails(coachingPlanWithAdditionalDetailsToDelete) }}
        maxWidth='xs'
        bottom=''
        position='fixed'
      />
    )}

    {coachingPlanWithTrackingToDelete != null && (
      <BannerModal
        title='Are you sure?'
        cancelButtonText='No, Cancel'
        confirmButtonText='Yes, Delete'
        dataTestId='confirmDeleteCoachingPlanTracking'
        bodyContent={'Are you sure you want to delete the tracking for this item?'}
        onClose={() => { setCoachingPlanWithTrackingToDelete(null) }}
        onConfirm={() => { void handleDeleteCoachingPlanTracking(coachingPlanWithTrackingToDelete) }}
        maxWidth='xs'
        bottom=''
        position='fixed'
      />
    )}

    <Card data-testid='cardContainer' sx={{ minHeight: '400px', width: '100%', mt: '1em', border: '1px solid var(--purple-500)', borderRadius: '8px', display: coachingPlans.length === 0 ? 'grid' : 'block' }}>
      {coachingPlans.length === 0 &&
        <Box sx={{ justifySelf: 'center', alignSelf: 'center', width: '300px' }}>
          <Box sx={{ justifySelf: 'center', mb: '.75em' }}>
            <HomeOutlined sx={{ color: 'var(--purple-300)', width: '150px', height: '150px' }} />
            <Typography variant='h4'>No Coaching Plan</Typography>
          </Box>
          {!props.readOnly &&
            <Typography variant='body1' sx={{ fontSize: '16px' }}>Use the Add Coaching Plan Button to add a Coaching Plan to your Annual Plan.</Typography>}
        </Box>
      }

      {coachingPlans.length > 0 &&
        <BasicTable
          indexRowIds
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadonly}
          dataTestId='csDevChecklistTable'
          columns={columns}
          rows={coachingPlanRows}
          isLoading={isLoading}
          onEdit={handleEditCoaching}
          onDelete={handleDeleteCoaching}
          hideExpand={props.hideExpand}
          implementationPhase={props.implementationPhase}
          hideDelete={props.isTracking}
          onUpdate={handleEditCoaching}
          onTrack={handleTrackCoaching}
          budgetOverviewView={props.budgetOverviewView}
        />
      }

      {showContinueDeleteCoaching &&
        <Modal
          title='Are you sure?'
          onClose={() => { setShowContinueDeleteCoaching(false) }}
          open={true}
          onConfirm={continueDeleteCoaching}
          bodyContent='Are you sure you want to delete this Coaching Plan?'
          confirmButtonText='Yes, Delete'
          cancelButtonText='No, Cancel'
        />
      }
    </Card>
  </Box>
}
